import { useCallback, useEffect, useRef } from 'react';

import { useFieldArray, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import useDesks from '@/hooks/useDesks';
import { applyFilters } from '@/modules/analytics/redux/actions';
import { selectAvailableChannels } from '@/redux/integrations/selectors';
import { AnalyticsType, LogsAnalytics } from '@/shared/types/analytics';
import { RootState } from '@/shared/types/state';
import { urlSafeBase64Decode, urlSafeBase64Encode } from '@/util/util';

import {
  createMutableCopy,
  filtersEqual,
  stableStringifyFilters,
  getDefaultValue,
  getValueKey,
  mapUiToDataModelType,
  inferFilterType,
  sanitizeHydratedFilters,
  shouldPersistFilter,
} from './sessionFilterUtils';

export interface FilterForm {
  filters: LogsAnalytics[];
}
/**
 * Hook for managing session filters with React Hook Form and Redux integration.
 *
 * Responsibilities:
 * - Hydration from URL on initial load and external URL changes (Redux remains the source of truth)
 * - Mirror Redux filters to URL with self-write suppression to avoid loops
 * - Providing form state (React Hook Form) for filter editors
 * - Defensive normalization for malformed filter objects (missing type)
 *
 * Side effects:
 * - Dispatches Redux actions (applyFilters)
 * - Navigates to update the URL query string
 */
export const useSessionFilters = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const filters = useSelector((state: RootState) => state.analytics.logs);
  const filtersLoaded = useSelector((state: RootState) => state.analytics.filtersLoaded);
  const availableChannels = useSelector(selectAvailableChannels);
  const { desks } = useDesks();

  // Track when we're currently saving to prevent reset during save operations
  const isSavingRef = useRef(false);

  const formMethods = useForm<FilterForm>({
    defaultValues: { filters: createMutableCopy(filters) },
  });

  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: 'filters',
  });

  const syncFormFilters = useCallback(
    (nextFilters: LogsAnalytics[]) => {
      formMethods.reset({ filters: createMutableCopy(nextFilters) });
    },
    [formMethods]
  );

  /**
   * Add a filter either by cloning the existing filter from Redux (if present) or
   * by appending a new filter with sensible defaults.
   */
  const addFilter = useCallback(
    (filterType: string) => {
      const dataModelType = mapUiToDataModelType(filterType);

      // Check if filter already exists in Redux state
      const existingFilter = filters.find((f) => f.type === dataModelType);

      if (existingFilter) {
        // Append a deep clone to make it fully mutable in the form
        append(createMutableCopy([existingFilter])[0]);
        return;
      }

      // Create new filter with default values
      const defaultValue = getDefaultValue(dataModelType);
      const propertyName = getValueKey(dataModelType);
      const value = Array.isArray(defaultValue)
        ? [...(defaultValue as unknown[])]
        : (defaultValue as LogsAnalytics[typeof propertyName]);

      const newFilter = { type: dataModelType, [propertyName]: value } as LogsAnalytics;
      // New tags filters match all tags; saved ones without a mode keep matching any.
      if (dataModelType === 'tags') {
        newFilter.tagsMode = 'all';
      }

      append(newFilter);
    },
    [append, filters]
  );

  /**
   * Remove a filter at a form index.
   */
  const removeFilter = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  /**
   * Persist current form filters to Redux (and indirectly to URL).
   * Applies defensive normalization and drops empty/duplicate-equivalent filters.
   */
  const saveFilters = useCallback(() => {
    // Set flag to prevent form reset during save
    isSavingRef.current = true;

    const filterValues = formMethods.getValues('filters');

    // Defensive normalization: ensure type is present by inferring from properties
    const normalized = filterValues.map((raw) => {
      const f = { ...raw };
      if (!f.type) {
        const inferred = inferFilterType(f);
        if (inferred) {
          f.type = inferred;
        }
      }
      return f;
    });

    const channelsTotal = Array.isArray(availableChannels) ? availableChannels.length : 0;
    const desksTotal = Array.isArray(desks) ? desks.length : 0;

    // Extract actual desk IDs from desks array
    const availableDesks = Array.isArray(desks) ? desks.map((desk) => desk.desk_id) : undefined;

    // Prune filters according to persistence rules
    const filtered = normalized.filter((f) =>
      shouldPersistFilter(f as LogsAnalytics, {
        channelsTotal,
        desksTotal,
        availableChannels: Array.isArray(availableChannels) ? availableChannels : undefined,
        availableDesks,
      })
    );

    // Skip dispatches when nothing actually changed to avoid Redux churn
    if (filtersEqual(filtered, filters)) {
      isSavingRef.current = false;
      return;
    }

    dispatch(applyFilters({ type: AnalyticsType.LOGS, filters: filtered }));
    syncFormFilters(filtered);

    // Clear saving flag after a brief delay to allow Redux update to complete
    setTimeout(() => {
      isSavingRef.current = false;
    }, 50);
  }, [dispatch, formMethods, availableChannels, desks, filters, syncFormFilters]);

  // Track last URL write to avoid hydrating our own updates
  const lastUrlFiltersRef = useRef<string | null>(null); // stores encoded token or '__none__'
  const wroteUrlRef = useRef(false);
  const ensureLoadedOnceRef = useRef(false);

  // Keep refs for Redux-driven values to avoid effect churn
  const filtersRef = useRef(filters);
  const filtersLoadedRef = useRef(filtersLoaded);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);
  useEffect(() => {
    filtersLoadedRef.current = filtersLoaded;
  }, [filtersLoaded]);

  // Hydrate from URL when it changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get('filters');
    const token = filterParam ?? '__none__';

    // Handle empty URL case once: mark filters as loaded while preserving current Redux filters
    if (!filterParam) {
      if (!ensureLoadedOnceRef.current && !filtersLoadedRef.current) {
        dispatch(applyFilters({ type: AnalyticsType.LOGS, filters: filtersRef.current }));
      }
      ensureLoadedOnceRef.current = true;
      return;
    }

    // Ignore our own writes (after writer modifies the URL)
    if (wroteUrlRef.current && token === (lastUrlFiltersRef.current ?? '__none__')) {
      return;
    }

    try {
      const decoded = urlSafeBase64Decode(filterParam);
      const parsedFilters = JSON.parse(decoded) as LogsAnalytics[];

      // Ensure types are present
      const normalized = parsedFilters.map((raw) => {
        const f = { ...raw };
        if (!f.type) {
          const inferred = inferFilterType(f);
          if (inferred) {
            f.type = inferred;
          }
        }
        return f;
      });

      // Apply sanitization cautiously: only drop "all-selected" for channels/environments when totals are known
      const channelsTotal = Array.isArray(availableChannels) ? availableChannels.length : 0;
      const desksTotal = Array.isArray(desks) ? desks.length : 0;

      // Extract actual desk IDs from desks array
      const availableDesks = Array.isArray(desks) ? desks.map((desk) => desk.desk_id) : undefined;

      const sanitized = sanitizeHydratedFilters(normalized, {
        channelsTotal,
        desksTotal,
        availableChannels: Array.isArray(availableChannels) ? availableChannels : undefined,
        availableDesks,
      });

      if (!filtersEqual(sanitized, filtersRef.current)) {
        dispatch(applyFilters({ type: AnalyticsType.LOGS, filters: sanitized }));
      }
    } catch {
      // ignore malformed URL
    }
    // Intentionally avoid depending directly on filters to reduce churn
  }, [availableChannels, desks, dispatch, location.search]);

  // Mirror Redux filters to URL; suppress self-originated hydration
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const current = params.get('filters');

    // On first run with URL param present: seed last-written token so future writes are allowed.
    if (lastUrlFiltersRef.current === null && current !== null) {
      lastUrlFiltersRef.current = current;
      return;
    }

    const encoded =
      filters.length > 0 ? urlSafeBase64Encode(stableStringifyFilters(filters)) : null;
    if ((encoded ?? '') === (current ?? '')) {
      return;
    }

    const next = new URLSearchParams(location.search);
    if (encoded) {
      next.set('filters', encoded);
    } else {
      next.delete('filters');
    }
    navigate({ search: next.toString() }, { replace: true });

    // Record our write to avoid hydrating our own updates
    lastUrlFiltersRef.current = encoded ?? '__none__';
    wroteUrlRef.current = true;
  }, [filters, location.search, navigate]);

  // Reset form when filters change from Redux
  useEffect(() => {
    // Skip reset if we're currently saving to prevent race conditions
    if (isSavingRef.current) {
      return;
    }

    const currentFormFilters = formMethods.getValues('filters');
    const filtersToUse = filters;

    // Use improved comparison that handles array ordering
    const hasChanged = !filtersEqual(currentFormFilters, filtersToUse);

    // Only reset if the filters have actually changed
    if (hasChanged) {
      formMethods.reset({ filters: createMutableCopy(filtersToUse) });
    }
  }, [filters, formMethods]);

  // Get active filters from Redux (single source of truth)
  const activeFilters = filters;

  /**
   * Load a specific set of filters (e.g., from a saved view)
   */
  const loadFilters = useCallback(
    (newFilters: LogsAnalytics[]) => {
      // Set flag to prevent form reset during load
      isSavingRef.current = true;

      dispatch(applyFilters({ type: AnalyticsType.LOGS, filters: newFilters }));
      syncFormFilters(newFilters);

      // Clear saving flag after a brief delay to allow Redux update to complete
      setTimeout(() => {
        isSavingRef.current = false;
      }, 50);
    },
    [dispatch, syncFormFilters]
  );

  /**
   * Clear all active filters
   */
  const clearAllFilters = useCallback(() => {
    // Set flag to prevent form reset during clear
    isSavingRef.current = true;

    dispatch(applyFilters({ type: AnalyticsType.LOGS, filters: [] }));
    syncFormFilters([]);

    // Clear saving flag after a brief delay to allow Redux update to complete
    setTimeout(() => {
      isSavingRef.current = false;
    }, 50);
  }, [dispatch, syncFormFilters]);

  /**
   * Remove a filter by type for external triggers (e.g., from FiltersBar).
   * - Special handling for insight filters: "insightFilter-<insightId>" removes a single insight.
   */
  const removeFilterByType = useCallback(
    (filterType: string) => {
      // Handle insight filter removal - format is "insightFilter-<insightId>"
      if (filterType.startsWith('insightFilter-')) {
        const insightId = filterType.replace('insightFilter-', '');
        const updatedFilters = activeFilters
          .map((filter) => {
            if (filter.type === 'insightFilters' && filter.insightFilters) {
              // Remove the specific insight filter
              const updatedInsightFilters = filter.insightFilters.filter(
                (insight) => insight.id !== insightId
              );

              // If no insight filters left, remove the entire filter
              if (updatedInsightFilters.length === 0) {
                return null;
              }

              return { ...filter, insightFilters: updatedInsightFilters };
            }
            return filter;
          })
          .filter(Boolean) as LogsAnalytics[];

        // Don't dispatch if both previous and next states are empty
        if (activeFilters.length === 0 && updatedFilters.length === 0) {
          return;
        }

        dispatch(applyFilters({ type: AnalyticsType.LOGS, filters: updatedFilters }));

        return;
      }

      // Handle regular filter removal
      const updatedFilters = activeFilters.filter((f) => f.type !== filterType);

      // Don't dispatch if both previous and next states are empty
      if (activeFilters.length === 0 && updatedFilters.length === 0) {
        return;
      }

      dispatch(applyFilters({ type: AnalyticsType.LOGS, filters: updatedFilters }));
    },
    [activeFilters, dispatch]
  );

  return {
    formMethods,
    fields,
    addFilter,
    removeFilter,
    saveFilters,
    activeFilters,
    removeFilterByType,
    loadFilters,
    clearAllFilters,
  };
};
